import { useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
} from 'antd';
import type { EnergyPackStatus } from '@goelectric/shared-types';
import { PACK_STATUS_LABELS, PACK_STATUS_VALUES } from '../utils/format';
import {
  CURRENCY_PATTERN,
  emptyPackFormValues,
  isValidVigencia,
  normalizeCurrency,
  PACK_DEFAULT_CURRENCY,
  PACK_KWH_MAX,
  PACK_KWH_MIN,
  PACK_NAME_MAX,
  PACK_PRICE_MAX,
  toAntdFieldPath,
  validatePackFormValues,
  type PackFormValues,
} from '../utils/packForm';

const STATUS_OPTIONS = PACK_STATUS_VALUES.map((value) => ({
  value,
  label: PACK_STATUS_LABELS[value],
}));

export interface PackFormProps {
  /** Valores iniciales (edición); por defecto, alta ACTIVE en COP. */
  initialValues?: PackFormValues;
  mode?: 'create' | 'edit';
  submitting?: boolean;
  onSubmit: (values: PackFormValues) => void | Promise<void>;
  onCancel?: () => void;
}

/**
 * Formulario de alta/edición de Packs de Energía (US-2.4).
 *
 * - Nombre, kWh y precio obligatorios; moneda ISO 4217 con default COP.
 * - Vigencia con `DatePicker` (fecha + hora); `validTo` opcional.
 * - Estado editable solo en la edición; el alta siempre nace ACTIVE.
 * - Validación inline con mensajes en español y red de seguridad antes de enviar.
 */
export function PackForm({
  initialValues,
  mode = 'create',
  submitting = false,
  onSubmit,
  onCancel,
}: PackFormProps) {
  const [form] = Form.useForm<PackFormValues>();
  const defaults = initialValues ?? emptyPackFormValues();
  const currency = Form.useWatch('currency', form) ?? defaults.currency ?? PACK_DEFAULT_CURRENCY;
  const isEdit = mode === 'edit';
  const [validationSummary, setValidationSummary] = useState<string[]>([]);

  /**
   * Red de seguridad final: las reglas inline dan feedback inmediato, pero acá se
   * vuelve a validar el modelo completo antes de enviar. Si hay problemas, se
   * mapean a paths de antd y no se llama a `onSubmit`.
   */
  const handleFinish = (values: PackFormValues) => {
    const issues = validatePackFormValues(values);
    if (issues.length > 0) {
      setValidationSummary(issues.map((issue) => issue.message));
      form.setFields(
        issues.map((issue) => ({
          name: toAntdFieldPath(issue.field),
          errors: [issue.message],
        })) as Parameters<typeof form.setFields>[0],
      );
      return;
    }

    setValidationSummary([]);
    void onSubmit({
      ...values,
      name: values.name?.trim() ?? '',
      currency: normalizeCurrency(values.currency) || PACK_DEFAULT_CURRENCY,
      // El estado no se renderiza en el alta: el contrato lo deja en ACTIVE.
      status: values.status ?? 'ACTIVE',
    });
  };

  return (
    <Form<PackFormValues>
      form={form}
      layout="vertical"
      initialValues={defaults}
      onFinish={handleFinish}
      onValuesChange={() => {
        if (validationSummary.length > 0) setValidationSummary([]);
      }}
      requiredMark={false}
      disabled={submitting}
    >
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        Datos del pack
      </Typography.Title>
      {validationSummary.length > 0 && (
        <Alert
          type="error"
          showIcon
          role="alert"
          aria-live="assertive"
          message="Revisa los datos del pack"
          description={
            <ul style={{ margin: 0, paddingInlineStart: 20 }}>
              {validationSummary.map((message, index) => (
                <li key={`${index}-${message}`}>{message}</li>
              ))}
            </ul>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Form.Item
        label="Nombre"
        name="name"
        rules={[
          { required: true, whitespace: true, message: 'Escribe el nombre del pack.' },
          {
            max: PACK_NAME_MAX,
            message: `El nombre no puede superar ${PACK_NAME_MAX} caracteres.`,
          },
        ]}
      >
        <Input
          placeholder="Ej. Pack 50 kWh"
          autoComplete="off"
          maxLength={PACK_NAME_MAX}
          showCount
        />
      </Form.Item>

      <Space size={16} wrap align="start">
        <Form.Item
          label="Energía (kWh)"
          name="kwh"
          style={{ minWidth: 220 }}
          rules={[
            { required: true, message: 'Ingresa los kWh del pack.' },
            {
              type: 'number',
              min: PACK_KWH_MIN,
              max: PACK_KWH_MAX,
              message: 'Los kWh deben ser mayores que 0.',
            },
          ]}
        >
          <InputNumber
            min={PACK_KWH_MIN}
            max={PACK_KWH_MAX}
            step={1}
            precision={2}
            aria-label="Energía (kWh)"
            style={{ width: 220 }}
            placeholder="50"
          />
        </Form.Item>

        <Form.Item
          label={`Precio (${currency})`}
          name="priceCop"
          style={{ minWidth: 220 }}
          rules={[
            { required: true, message: 'Ingresa el precio del pack.' },
            {
              type: 'number',
              min: 0,
              max: PACK_PRICE_MAX,
              message: 'El precio debe ser mayor o igual a 0.',
            },
          ]}
        >
          <InputNumber
            min={0}
            max={PACK_PRICE_MAX}
            step={1000}
            precision={2}
            aria-label="Precio del pack"
            style={{ width: 240 }}
            placeholder="60000"
          />
        </Form.Item>

        <Form.Item
          label="Moneda"
          name="currency"
          style={{ minWidth: 140 }}
          normalize={(value: string) => normalizeCurrency(value)}
          rules={[
            { required: true, message: 'Ingresa la moneda.' },
            {
              pattern: CURRENCY_PATTERN,
              message: 'Usa un código ISO 4217 de 3 letras (por ejemplo COP).',
            },
          ]}
        >
          <Input
            maxLength={3}
            style={{ width: 140, textTransform: 'uppercase' }}
            aria-label="Moneda"
            placeholder="COP"
          />
        </Form.Item>
      </Space>

      <Space size={16} wrap align="start">
        <Form.Item
          label="Vigente desde"
          name="validFrom"
          style={{ minWidth: 240 }}
        >
          <DatePicker
            showTime={{ format: 'HH:mm' }}
            format="DD/MM/YYYY HH:mm"
            placeholder="Selecciona fecha y hora"
            style={{ width: 240 }}
          />
        </Form.Item>
        <Form.Item
          label="Vigente hasta (opcional)"
          name="validTo"
          dependencies={['validFrom']}
          style={{ minWidth: 240 }}
          rules={[
            {
              validator: async (_, value) => {
                const from = form.getFieldValue('validFrom') as PackFormValues['validFrom'];
                if (!value || isValidVigencia(from, value)) return;
                throw new Error('La fecha de fin debe ser posterior a la fecha de inicio.');
              },
            },
          ]}
        >
          <DatePicker
            showTime={{ format: 'HH:mm' }}
            format="DD/MM/YYYY HH:mm"
            placeholder="Sin fecha de fin"
            style={{ width: 240 }}
          />
        </Form.Item>
      </Space>

      {isEdit && (
        <Form.Item
          label="Estado"
          name="status"
          style={{ maxWidth: 240 }}
          rules={[{ required: true, message: 'Elige el estado del pack.' }]}
        >
          <Select<EnergyPackStatus> options={STATUS_OPTIONS} aria-label="Estado del pack" />
        </Form.Item>
      )}

      <Divider />

      <Space>
        <Button type="primary" htmlType="submit" loading={submitting}>
          {isEdit ? 'Guardar cambios' : 'Crear pack'}
        </Button>
        {onCancel && (
          <Button onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
        )}
      </Space>
    </Form>
  );
}
